import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-03.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import {
  IncrementDecrementSize,
  RedoIncrementDecrement,
} from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoInputSize } from "@redotech/redo-web/arbiter-components/input/base-redo-text-input";
import { RedoTagInputField } from "@redotech/redo-web/arbiter-components/input/redo-tag-input-field";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  RedoTextareaInputField,
  RedoTextareaInputFieldSize,
  RedoTextareaInputFieldTheme,
} from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import {
  RadioGroupOption,
  RedoRadioGroup,
} from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import {
  RedoDropdownInputSize,
  RedoSingleSelectDropdownInput,
} from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { TimeOfDay } from "@redotech/redo-web/arbiter-components/time/redo-time-picker";
import {
  RedoTimeRangePicker,
  TimeOfDayRange,
} from "@redotech/redo-web/arbiter-components/time/redo-time-range-picker";
import {
  RedoToggle,
  RedoToggleSize,
} from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import * as cardCss from "@redotech/redo-web/card.module.css";
import { Flex } from "@redotech/redo-web/flex";
import { LabeledInput, LabelTheme } from "@redotech/redo-web/labeled-input";
import { ActionPortalContext, Actions } from "@redotech/redo-web/page";
import { Slider } from "@redotech/redo-web/slider";
import { Text } from "@redotech/redo-web/text";
import { createFormHook, createFormHookContexts } from "@tanstack/react-form";
import { ReactNode, useContext, useState } from "react";
import { createPortal } from "react-dom";

const MINUTES_IN_DAY = 1440;
const MINUTES_IN_HOUR = 60;

export const { fieldContext, useFieldContext, formContext, useFormContext } =
  createFormHookContexts();
function TextField({
  label,
  placeholder,
  description,
  size,
  maxLength,
}: {
  label: string;
  placeholder?: string;
  description?: string;
  size?: RedoInputSize;
  maxLength?: number;
}) {
  const field = useFieldContext<string>();
  return (
    <RedoTextInput
      description={description}
      errorText={field.state.meta.errors
        ?.map((error) => error?.message)
        .join(", ")}
      label={label}
      maxLength={maxLength}
      placeholder={placeholder}
      setValue={(value) => field.handleChange(value)}
      size={size}
      value={field.state.value}
    />
  );
}

function NumberField({
  label,
  description,
  min,
  max,
  size,
}: {
  label?: string;
  description?: string;
  min?: number;
  max?: number;
  size?: RedoInputSize;
}) {
  const field = useFieldContext<number>();
  return (
    <RedoTextInput
      description={description}
      errorText={field.state.meta.errors
        ?.map((error) => error?.message)
        .join(", ")}
      inputTypeHint="number"
      label={label}
      max={max}
      min={min}
      setValue={(value) => field.handleChange(parseInt(value) || 0)}
      size={size}
      value={(field.state.value ?? 0).toString()}
    />
  );
}

function CheckboxField({
  label,
  disabled,
  indeterminateValue = false,
  size = "sm",
}: {
  label: string;
  disabled?: boolean;
  indeterminateValue?: boolean;
  size?: "xs" | "sm" | "md" | undefined;
}) {
  const field = useFieldContext<boolean>();
  return (
    <RedoCheckbox
      disabled={disabled}
      label={label}
      setValue={(value) =>
        field.handleChange(
          value === "indeterminate" ? indeterminateValue : value,
        )
      }
      size={size}
      value={field.state.value}
    />
  );
}

function TextAreaField({
  label,
  placeholder,
  disabled,
  hintText,
  autoExpand,
  size = RedoTextareaInputFieldSize.SMALL,
  theme,
  maxLength,
}: {
  label: string;
  placeholder?: string;
  disabled?: boolean;
  hintText?: string;
  autoExpand?: boolean;
  size?: RedoTextareaInputFieldSize;
  theme?: RedoTextareaInputFieldTheme;
  maxLength?: number;
}) {
  const field = useFieldContext<string>();
  return (
    <RedoTextareaInputField
      autoExpand={autoExpand}
      disabled={disabled}
      errorText={field.state.meta.errors
        ?.map((error) => error?.message)
        .join(", ")}
      hintText={hintText}
      label={label}
      maxLength={maxLength}
      placeholder={placeholder}
      setValue={(value) => field.handleChange(value)}
      size={size}
      theme={theme}
      value={field.state.value}
    />
  );
}

function ToggleField({
  label,
  description,
  disabled,
  size = "xs",
  style = "desktop",
  labelPosition = "right",
}: {
  label: string;
  description?: string;
  disabled?: boolean;
  size?: RedoToggleSize;
  style?: "desktop" | "mobile";
  labelPosition?: "left" | "right";
}) {
  const field = useFieldContext<boolean>();
  return (
    <RedoToggle
      description={description}
      disabled={disabled}
      label={label}
      labelPosition={labelPosition}
      setValue={(value) => field.handleChange(value)}
      size={size}
      style={style}
      value={field.state.value}
    />
  );
}

function SelectField<T>({
  label,
  placeholder,
  options,
  size = "xs",
  description,
}: {
  label: ReactNode;
  placeholder?: string;
  options: RedoListItem<T>[];
  size?: RedoDropdownInputSize;
  description?: string;
}) {
  const field = useFieldContext<T>();
  return (
    <RedoSingleSelectDropdownInput
      description={description}
      label={label}
      options={options}
      optionSelected={({ item }) => field.handleChange(item.value)}
      placeholder={placeholder}
      selectedItem={options.find((o) => o.value === field.state.value)}
      size={size}
    />
  );
}

function SliderField({
  label,
  min,
  max,
  step,
  leftLabel,
  rightLabel,
}: {
  label: string;
  min: number;
  max: number;
  step: number;
  leftLabel: ReactNode;
  rightLabel: ReactNode;
}) {
  const field = useFieldContext<number>();
  return (
    <Flex dir="column" gap="xs" pb="lg">
      <LabeledInput label={label} theme={LabelTheme.THIN_BOLD}>
        <Flex justify="space-between" w="full">
          <Text fontSize="sm" textColor="secondary">
            {leftLabel}
          </Text>
          <Text fontSize="sm" textColor="secondary">
            {rightLabel}
          </Text>
        </Flex>
        <Slider
          max={max}
          min={min}
          onChange={field.handleChange}
          step={step}
          value={field.state.value}
        />
      </LabeledInput>
    </Flex>
  );
}

function TagInputField({
  label,
  placeholder,
  size = "sm",
}: {
  label: string;
  placeholder?: string;
  size?: RedoInputSize;
}) {
  const field = useFieldContext<string[]>();
  const [inputValue, setInputValue] = useState("");

  return (
    <RedoTagInputField
      delimiters={["whitespace", "newline"]}
      inputValue={inputValue || ""}
      label={label}
      placeholder={placeholder || ""}
      setInputValue={(value) => setInputValue(value || "")}
      setTags={(newTags) => field.handleChange(newTags)}
      size={size}
      tags={(field.state.value || []).map((tag) => ({ text: tag, id: tag }))}
    />
  );
}

function IncrementDecrementField({
  min,
  max,
  step = 1,
  size = "small",
}: {
  min?: number;
  max?: number;
  step?: number;
  size?: IncrementDecrementSize;
}) {
  const field = useFieldContext<number>();

  return (
    <RedoIncrementDecrement
      max={max}
      min={min}
      setValue={(value) => field.handleChange(value)}
      size={size}
      step={step}
      value={field.state.value || 0}
    />
  );
}

export enum DurationUnit {
  MINUTES = "minutes",
  HOURS = "hours",
  DAYS = "days",
}

const convertMinutesToDisplay = (
  minutes: number | null | undefined,
  supportedUnits: DurationUnit[],
  preferredUnit?: DurationUnit,
): { value: number; unit: DurationUnit } => {
  if (!minutes) {
    const defaultUnit = supportedUnits.includes(DurationUnit.DAYS)
      ? DurationUnit.DAYS
      : supportedUnits.includes(DurationUnit.HOURS)
        ? DurationUnit.HOURS
        : supportedUnits[0];
    return { value: 1, unit: defaultUnit };
  }

  if (preferredUnit && supportedUnits.includes(preferredUnit)) {
    const multiplier = getUnitMultiplier(preferredUnit);
    return { value: Math.round(minutes / multiplier), unit: preferredUnit };
  }

  if (
    supportedUnits.includes(DurationUnit.DAYS) &&
    minutes >= MINUTES_IN_DAY &&
    minutes % MINUTES_IN_DAY === 0
  ) {
    return {
      value: Math.round(minutes / MINUTES_IN_DAY),
      unit: DurationUnit.DAYS,
    };
  } else if (
    supportedUnits.includes(DurationUnit.HOURS) &&
    minutes >= MINUTES_IN_HOUR &&
    minutes % MINUTES_IN_HOUR === 0
  ) {
    return {
      value: Math.round(minutes / MINUTES_IN_HOUR),
      unit: DurationUnit.HOURS,
    };
  } else if (supportedUnits.includes(DurationUnit.MINUTES)) {
    return { value: minutes, unit: DurationUnit.MINUTES };
  } else {
    const unit = supportedUnits[0];
    const multiplier = getUnitMultiplier(unit);
    return { value: Math.round(minutes / multiplier), unit };
  }
};

const getUnitMultiplier = (unit: DurationUnit): number => {
  switch (unit) {
    case DurationUnit.MINUTES:
      return 1;
    case DurationUnit.HOURS:
      return MINUTES_IN_HOUR;
    case DurationUnit.DAYS:
      return MINUTES_IN_DAY;
    default:
      return 1;
  }
};

const convertDisplayToMinutes = (value: number, unit: DurationUnit): number => {
  const multiplier = getUnitMultiplier(unit);
  return value * multiplier;
};

function DurationField({
  label,
  unitOptions,
  size = "sm",
}: {
  label?: string;
  unitOptions: RedoListItem<DurationUnit>[];
  size?: RedoInputSize;
}) {
  const field = useFieldContext<number>();
  const supportedUnits = unitOptions.map((option) => option.value);
  const [selectedUnit, setSelectedUnit] = useState<DurationUnit | undefined>();

  const displayValue = convertMinutesToDisplay(
    field.state.value,
    supportedUnits,
    selectedUnit,
  );

  // Update selectedUnit if it changed (for initial load)
  if (!selectedUnit || selectedUnit !== displayValue.unit) {
    setSelectedUnit(displayValue.unit);
  }

  const handleValueChange = (newValue: string) => {
    const numValue = parseInt(newValue) || 1;
    const minutes = convertDisplayToMinutes(numValue, displayValue.unit);
    field.handleChange(minutes);
  };

  const handleUnitChange = (newUnit: DurationUnit) => {
    setSelectedUnit(newUnit);
    const minutes = convertDisplayToMinutes(displayValue.value, newUnit);
    field.handleChange(minutes);
  };

  const hasError =
    field.state.meta.errors && field.state.meta.errors.length > 0;
  const errorText = field.state.meta.errors.join(", ");

  return (
    <Flex dir="column" gap="xs" ml="xl">
      {label && (
        <Text fontSize="xs" fontWeight="regular" mb="xs" textColor="tertiary">
          {label}
        </Text>
      )}
      <Flex align="center" dir="row" gap="md">
        <div style={{ width: "80px" }}>
          <RedoTextInput
            fullWidth
            inputTypeHint="number"
            min={1}
            setValue={handleValueChange}
            size={size}
            value={displayValue.value.toString()}
          />
        </div>
        <div style={{ width: "160px" }}>
          <RedoSingleSelectDropdownInput
            options={unitOptions}
            optionSelected={({ item }) => handleUnitChange(item.value)}
            selectedItem={unitOptions.find(
              (item) => item.value === displayValue.unit,
            )}
          />
        </div>
      </Flex>
      {hasError && (
        <Text fontSize="sm" textColor="error">
          {errorText}
        </Text>
      )}
    </Flex>
  );
}

function ChipInputField({
  label,
  description,
  placeholder,
}: {
  label: string;
  description?: string;
  placeholder?: string;
}) {
  const field = useFieldContext<string[]>();
  const [inputValue, setInputValue] = useState("");

  const hasError =
    field.state.meta.errors && field.state.meta.errors.length > 0;
  const errorText = field.state.meta.errors
    ?.map((error) => error?.message)
    .join(", ");

  return (
    <Flex dir="column" gap="xs" w="full">
      <RedoTagInputField
        delimiters={["newline"]}
        description={description}
        inputValue={inputValue || ""}
        label={label}
        placeholder={placeholder || ""}
        setInputValue={(value) => setInputValue(value || "")}
        setTags={(newTags) => field.handleChange(newTags)}
        size="sm"
        state={hasError ? "error" : "default"}
        tags={(field.state.value || []).map((tag) => ({ text: tag, id: tag }))}
      />
      {hasError && (
        <Text fontSize="sm" textColor="error">
          {errorText}
        </Text>
      )}
    </Flex>
  );
}

function RadioGroupField<T extends string>({
  options,
  size = "sm",
}: {
  options: RadioGroupOption<T>[];
  size?: "sm" | "md";
}) {
  const field = useFieldContext<T>();
  return (
    <RedoRadioGroup
      options={options}
      setValue={(value) => field.handleChange(value)}
      size={size}
      value={field.state.value}
    />
  );
}

function TimeBlocksField({ disabled }: { disabled?: boolean }) {
  const field =
    useFieldContext<{ start?: string | null; end?: string | null }[]>();

  const timeBlocks = field.state.value || [];

  function convertStringToTimeOfDay(
    timeString: string | null | undefined,
  ): TimeOfDay | null {
    if (!timeString) return null;
    const [hours, minutes] = timeString.split(":").map(Number);
    return { hours, minutes };
  }

  function convertTimeOfDayToString(timeOfDay: TimeOfDay): string {
    return `${timeOfDay.hours.toString().padStart(2, "0")}:${timeOfDay.minutes.toString().padStart(2, "0")}`;
  }

  function convertStringRangeToTimeOfDayRange(
    start: string | null | undefined,
    end: string | null | undefined,
  ): TimeOfDayRange | null {
    const startTime = convertStringToTimeOfDay(start || "09:00");
    const endTime = convertStringToTimeOfDay(end || "17:00");

    if (!startTime || !endTime) return null;

    return { start: startTime, end: endTime };
  }

  function handleRemoveTimeBlock(blockIndex: number) {
    const newTimeBlocks = timeBlocks.filter((_, index) => index !== blockIndex);
    field.handleChange(newTimeBlocks);
  }

  function handleAddTimeBlock() {
    const newBlock = { start: "09:00", end: "17:00" };
    field.handleChange([...timeBlocks, newBlock]);
  }

  function handleTimeBlockChange(
    blockIndex: number,
    newRange: TimeOfDayRange | null,
  ) {
    const newTimeRange = {
      start: newRange ? convertTimeOfDayToString(newRange.start) : undefined,
      end: newRange ? convertTimeOfDayToString(newRange.end) : undefined,
    };
    const newTimeBlocks = timeBlocks.map((b, index) =>
      index === blockIndex ? newTimeRange : b,
    );
    field.handleChange(newTimeBlocks);
  }

  return (
    <Flex dir="column" gap="sm">
      {timeBlocks.length > 0 ? (
        timeBlocks.map((block, i) => (
          <Flex align="center" gap="sm" key={i} w="full">
            <RedoButton
              hierarchy="tertiary"
              IconLeading={TrashIcon}
              onClick={() => handleRemoveTimeBlock(i)}
              size="md"
            />
            <RedoTimeRangePicker
              clearIcon={null}
              setValue={(newRange: TimeOfDayRange | null) =>
                handleTimeBlockChange(i, newRange)
              }
              value={convertStringRangeToTimeOfDayRange(block.start, block.end)}
            />
            {i === timeBlocks.length - 1 && (
              <RedoButton
                hierarchy="tertiary"
                IconLeading={PlusIcon}
                onClick={handleAddTimeBlock}
                size="sm"
              />
            )}
          </Flex>
        ))
      ) : (
        <RedoButton
          disabled={disabled}
          hierarchy="tertiary"
          IconLeading={PlusIcon}
          onClick={handleAddTimeBlock}
          size="md"
          text="Add time block"
        />
      )}
    </Flex>
  );
}

function SaveAndCancelActions() {
  const actionsPortal = useContext(ActionPortalContext);
  const form = useFormContext();
  return (
    <>
      {actionsPortal &&
        createPortal(
          <form.Subscribe
            selector={(state) => ({
              isDefaultValue: state.isDefaultValue,
              isSubmitting: state.isSubmitting,
              canSubmit: state.canSubmit,
            })}
          >
            {({ isDefaultValue, isSubmitting, canSubmit }) => (
              <Actions show={!isDefaultValue || isSubmitting}>
                <div className={cardCss.buttonBar}>
                  <RedoButton
                    disabled={!canSubmit || isDefaultValue}
                    hierarchy="primary"
                    onClick={() => form.handleSubmit()}
                    pending={isSubmitting}
                    size="md"
                    text="Save"
                    type="submit"
                  />
                  <RedoButton
                    disabled={isSubmitting || isDefaultValue}
                    hierarchy="secondary"
                    onClick={() => form.reset()}
                    size="md"
                    text="Cancel"
                    type="reset"
                  />
                </div>
              </Actions>
            )}
          </form.Subscribe>,
          actionsPortal,
        )}
    </>
  );
}

export const { useAppForm, withForm } = createFormHook({
  fieldContext,
  formContext,
  fieldComponents: {
    TextField,
    NumberField,
    CheckboxField,
    TextAreaField,
    ToggleField,
    SelectField,
    SliderField,
    TagInputField,
    IncrementDecrementField,
    DurationField,
    ChipInputField,
    RadioGroupField,
    TimeBlocksField,
  },
  formComponents: { SaveAndCancelActions },
});
